import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Modal from './Modal';
import AssetPicker from './AssetPicker';
import {
  deviceName, warrantyInWords, LIFECYCLE_LABEL, OPERATIONAL_LABEL,
  lifecycleTone, operationalTone, formatDay, holderLabel, tagText } from '../lib/assets';
import TagChip from './asset/TagChip';
import { updatesLine } from './asset/assetView';
import './Assets.css';

/**
 * The device a ticket is about, beside the ticket's own details.
 *
 * This is the whole point of the register: whoever picks the ticket up sees
 * which machine it is, whether it is still under warranty, and the last few
 * things that went wrong with it - before they touch it.
 *
 * The SERVER decides what is in `asset`. A contractor's card arrives without
 * the holder, the place or the cost; a member of staff's arrives without the
 * cost unless the register lets them read it. This component renders what it
 * is given and never assumes a field is there, so nothing here has to know
 * who is looking.
 *
 * `canAttach` is for a ticket with no device yet: an administrator may add
 * one from here, which is how a ticket raised about an unregistered laptop
 * gets its device once IT has entered it.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function AssetCard({ asset, ticketId, requesterId, onChanged,
  canAttach = false, contractor = false, canRecordRepair = false, authHeaders = null }) {
  const [changing, setChanging] = useState(false);
  const [choice, setChoice] = useState(asset || null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const mayChange = Boolean(asset ? asset.can_change : canAttach) && !contractor;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API_BASE}/tickets/${ticketId}`, { asset_id: choice ? choice.id : null });
      setChanging(false);
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not change the device.');
    } finally {
      setSaving(false);
    }
  };

  const dialog = changing && (
    <Modal
      title={asset ? 'Change the device' : 'Which device is this about?'}
      subtitle="The ticket’s history and both devices’ histories will say who changed it."
      onClose={() => setChanging(false)}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={() => setChanging(false)}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={save}
            disabled={saving || (choice?.id || null) === (asset?.id || null)}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <AssetPicker value={choice} onChange={setChoice} requesterId={requesterId} />
      {error && <p className="as-error" role="alert">{error}</p>}
    </Modal>
  );

  if (!asset) {
    if (!mayChange) return null;
    return (
      <div className="as-card" data-empty="true">
        <div className="as-card-head">
          <span className="as-quiet">No device is named on this ticket.</span>
          <span className="as-spacer" />
          <button type="button" className="btn btn-plain btn-small"
            onClick={() => { setChoice(null); setChanging(true); }}>
            Add a device
          </button>
        </div>
        {dialog}
      </div>
    );
  }

  const warranty = warrantyInWords(asset.warranty_end);
  const specs = (asset.spec_schema || [])
    .map((f) => [f.label, asset.specs?.[f.key]])
    .filter(([, v]) => v !== undefined && v !== null && v !== '');
  const others = asset.recent_tickets || [];

  return (
    <section className="as-card" aria-label="The device this ticket is about">
      <div className="as-card-head">
        <TagChip asset={asset} />
        {contractor
          ? <span className="as-card-title">{deviceName(asset)}</span>
          : <Link className="as-card-title" to={`/assets/${asset.id}`}>{deviceName(asset)}</Link>}
        <span className="as-chip" data-tone={lifecycleTone(asset.lifecycle_status)}>
          {LIFECYCLE_LABEL[asset.lifecycle_status] || asset.lifecycle_status}
        </span>
        {asset.operational_status && asset.operational_status !== 'operational' && (
          <span className="as-chip" data-tone={operationalTone(asset.operational_status)}>
            {OPERATIONAL_LABEL[asset.operational_status]}
          </span>
        )}
        <span className="as-spacer" />
        {mayChange && (
          <button type="button" className="btn btn-plain btn-small"
            onClick={() => { setChoice(asset); setChanging(true); }}>
            Change
          </button>
        )}
      </div>

      <p className="as-card-line">
        <span className="as-chip" data-tone={warranty.tone}>{warranty.text}</span>
      </p>
      <p className="as-card-line">
        {[asset.category_name,
          asset.serial_number ? `Serial ${asset.serial_number}` : null,
          asset.holder_name ? `With ${holderLabel(asset)}` : (asset.holder_name === null ? 'Not assigned to anyone' : null),
          asset.location_path].filter(Boolean).join(' · ')}
      </p>
      {specs.length > 0 && (
        <p className="as-card-line">{specs.map(([k, v]) => `${k}: ${v}`).join(' · ')}</p>
      )}
      {/* What its agent last said that often answers the ticket: is the
          disk encrypted, is it waiting to restart, is it nearly full. */}
      {asset.reported?.reported_at && (
        <p className="as-card-line">
          {[`Reported ${new Date(asset.reported.reported_at).toLocaleDateString('en-GB')}`,
            asset.reported.encryption === 'on' ? 'disk encrypted'
              : asset.reported.encryption === 'off' ? 'disk NOT encrypted' : null,
            asset.reported.patching?.needs ? `needs patching (${asset.reported.patching.reasons.join('; ')})`
              : asset.reported.pending_reboot ? 'waiting to restart' : null,
            asset.reported.fullest_disk?.used_pct >= 90
              ? `${asset.reported.fullest_disk.name} ${asset.reported.fullest_disk.used_pct}% full` : null]
            .filter(Boolean).join(' · ')}
        </p>
      )}
      {asset.reported && <DeviceReport r={asset.reported} />}
      {!contractor && asset.access && (
        <p className="as-card-line">
          <Link to={`/assets/${asset.id}`}>Open the device page</Link>
          <span className="as-quiet"> — its full history, location and documents</span>
        </p>
      )}
      {asset.cost_ex_vat !== undefined && asset.cost_ex_vat !== null && (
        <p className="as-card-line">
          {`Cost ${Number(asset.cost_ex_vat).toFixed(2)} ${asset.currency || ''} excl. VAT`}
          {asset.supplier ? ` · from ${asset.supplier}` : ''}
          {asset.purchase_date ? ` · bought ${formatDay(asset.purchase_date)}` : ''}
        </p>
      )}

      {others.length > 0 && (
        <>
          <p className="as-card-line">
            {asset.ticket_count - 1 > others.length
              ? `The last ${others.length} of ${asset.ticket_count - 1} other tickets on this device:`
              : `${others.length} other ticket${others.length === 1 ? '' : 's'} on this device:`}
          </p>
          <ul className="as-card-tickets">
            {others.map((t) => (
              <li key={t.id}>
                {t.may_open && !contractor
                  ? <Link to={`/tickets/${t.id}`}>{t.ticket_number}</Link>
                  : <span className="as-tag">{t.ticket_number}</span>}
                {t.title ? ` — ${t.title}` : ''}
                <span className="as-quiet">{` · ${t.status || ''} · ${formatDay(t.created_at)}`}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {canRecordRepair && ticketId && (
        <RepairNote ticketId={ticketId} asset={asset} authHeaders={authHeaders} />
      )}
      {dialog}
    </section>
  );
}

/**
 * "Replaced the battery" - written by whoever is doing the work, into the
 * device's own history as well as this ticket's. The server decides who may
 * (the contractor on the job, a linked worker, a register writer) and only
 * while the ticket is open; this box only appears where that is likely.
 */
function RepairNote({ ticketId, asset, authHeaders }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const [error, setError] = useState('');
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    setSaid('');
    try {
      await axios.post(`${API_BASE}/tickets/${ticketId}/asset-note`, { text, kind: 'repaired' },
        authHeaders ? { headers: authHeaders } : undefined);
      setSaid(`Recorded in ${tagText(asset)}’s history.`);
      setText('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not record that.');
    } finally { setBusy(false); }
  };
  return (
    <form className="as-repair" onSubmit={send}>
      <label htmlFor="as-repair-text" className="as-picker-label">What did you do to the device?</label>
      <textarea id="as-repair-text" rows={2} value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Replaced the battery; reseated the RAM" />
      {error && <p className="as-error" role="alert">{error}</p>}
      {said && <p className="as-quiet" role="status">{said}</p>}
      <button type="submit" className="btn btn-secondary btn-small" disabled={busy || !text.trim()}>
        {busy ? 'Saving…' : 'Add to the device’s history'}
      </button>
    </form>
  );
}

const stateWord = (s) => ({ on: 'On', off: 'Off' }[s] || 'Not known');
const seenWords = (t) => {
  if (!t) return 'never';
  const mins = Math.round((Date.now() - new Date(t).getTime()) / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins} minutes ago`;
  const hours = Math.round(mins / 60);
  if (hours < 36) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  return `${Math.round(hours / 24)} days ago`;
};

/**
 * What the device's agent last said about itself, for whoever is fixing the
 * ticket - the contractor included, who cannot open the device page. The
 * server has already left out where it is and its network addresses, and
 * who is signed in unless the viewer may read the register.
 */
function DeviceReport({ r }) {
  const a = r.agent || {};
  if (!a.installed && !r.reported_at) return null;
  return (
    <details className="as-card-report" data-testid="device-report">
      <summary>
        What the device reports
        <span className="as-quiet">
          {a.installed
            ? ` · agent ${a.version || ''} last in touch ${seenWords(a.last_seen_at)}`
            : ' · its agent has been stopped; this is the last report'}
        </span>
      </summary>
      {!r.reported_at ? <p className="as-quiet">Its agent is installed but has not sent a report yet.</p> : (
        <dl className="as-facts">
          <dt>System</dt>
          <dd>{[r.os, r.os_build && `build ${r.os_build}`].filter(Boolean).join(', ') || '—'}{r.pending_reboot ? ' · waiting to restart' : ''}</dd>
          {r.updates && <><dt>Windows Update</dt><dd>{updatesLine(r.updates)}</dd></>}
          {r.last_boot && <><dt>Last started</dt><dd>{new Date(r.last_boot).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</dd></>}
          {'signed_in' in r && <><dt>Signed in</dt><dd>{r.signed_in || 'Nobody'}</dd></>}
          <dt>Encryption</dt><dd>{stateWord(r.encryption)}{r.encryption_method ? ` · ${r.encryption_method}` : ''}</dd>
          <dt>Firewall</dt><dd>{stateWord(r.firewall)}</dd>
          <dt>Secure Boot</dt><dd>{stateWord(r.secure_boot)}</dd>
          <dt>Protection</dt><dd>{(r.endpoint_protection || []).join(', ') || 'None reported'}</dd>
          <dt>Processor</dt><dd>{[r.cpu, r.cores && `${r.cores} cores`].filter(Boolean).join(' · ') || '—'}</dd>
          <dt>Memory</dt><dd>{r.ram_gb ? `${r.ram_gb} GB` : '—'}</dd>
          <dt>Disks</dt>
          <dd>
            {(r.disks || []).length === 0 ? '—' : r.disks.map((d) => (
              <span key={d.name} style={{ display: 'block' }}>
                {`${d.name} ${d.free_gb} GB free of ${d.size_gb} GB`}
                {d.size_gb ? ` (${Math.round(((d.size_gb - d.free_gb) / d.size_gb) * 100)}% full)` : ''}
              </span>
            ))}
          </dd>
          {(r.battery_health_pct !== null && r.battery_health_pct !== undefined) && (
            <><dt>Battery</dt><dd>{`Holds ${r.battery_health_pct}% of its design charge${r.battery_cycles ? `, ${r.battery_cycles} cycles` : ''}`}</dd></>
          )}
          <dt>Reported</dt><dd>{new Date(r.reported_at).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}</dd>
        </dl>
      )}
    </details>
  );
}
